import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { CommonStoreStatus } from './CommonStoreStatus'
import { Event, IdentityRole } from '../model/Event'
import { QuickProvisioningIdentity, Identity } from '../model/Identity'
import { 
  EventIdentityProvisionRequest, 
  EventIdentityProvisionMultipleRequest 
} from '../requests/EventIdentityProvisionRequest'

import EventEndpoints from '../endpoints/EventEndpoints'
import IdentityEndpoints from '../endpoints/IdentityEndpoints'
import * as util from '../common/Util'

interface QuickProvisioningState {
  // Core state
  status: CommonStoreStatus
  event: Event | null
  identities: Array<QuickProvisioningIdentity>
  
  // Loading management
  waitingFor: number
  
  // Actions
  initialize: (eventId: number) => void
  refresh: () => Promise<void>
  registerFreshIdentity: (identity: Identity) => void
  provisionIdentity: (
    identityId: number, 
    request: EventIdentityProvisionRequest
  ) => Promise<QuickProvisioningIdentity>
  deleteProvisioning: (identityId: number) => Promise<QuickProvisioningIdentity>
  provisionMultipleForEvent: (
    request: EventIdentityProvisionMultipleRequest
  ) => Promise<void>
  
  // Loading helpers
  startLoading: () => void
  doneLoading: () => void
  
  // Computed getters
  isLoading: () => boolean
  identitiesSorted: () => Array<QuickProvisioningIdentity>
  orderedRoles: () => Array<IdentityRole>
}

// Factory function to create quick provisioning store instances
export const createQuickProvisioningStore = (eventId: number) => {
  return create<QuickProvisioningState>()(
    devtools(
      (set, get) => ({
        // Initial state
        status: CommonStoreStatus.LOADING,
        event: null,
        identities: [],
        waitingFor: 0,
        
        // Actions
        initialize: (id: number) => {
          // Store the event ID for use in actions
          ;(get() as any)._eventId = id
        },
        
        refresh: async () => {
          const { startLoading, doneLoading } = get()
          const eventId = (get() as any)._eventId
          
          startLoading()
          
          return new Promise<void>((resolve, reject) => {
            let n = 0
            const Done = () => {
              if (++n >= 2) {
                set({ status: CommonStoreStatus.READY })
                doneLoading()
                resolve()
              }
            }
            
            EventEndpoints.getEvent(eventId)
              .then(event => {
                set({ event })
                Done()
              })
              .catch(err => {
                console.error('Error in QuickProvisioningStore', err)
                set({ status: CommonStoreStatus.ERROR })
                doneLoading()
                reject(err)
              })
            
            IdentityEndpoints.getPartialIdentityList(eventId)
              .then(identities => {
                set({ identities })
                Done()
              })
              .catch(err => {
                console.error('Error in QuickProvisioningStore', err)
                set({ status: CommonStoreStatus.ERROR })
                doneLoading()
                reject(err)
              })
          })
        },
        
        registerFreshIdentity: (identity: Identity) => {
          // This is typically used when an identity is created outside
          // of this store and needs to be loaded in.
          const copy: QuickProvisioningIdentity = Object.assign(
            {
              roleId: null,
              accreditationLevelId: null,
              secondaryRoleName: null
            },
            util.DeepCopy(identity)
          )
          
          set((state) => ({
            identities: [...state.identities, copy]
          }))
        },
        
        provisionIdentity: async (
          identityId: number, 
          request: EventIdentityProvisionRequest
        ) => {
          const { event, identities } = get()
          if (!event) throw new Error('Event not loaded')
          
          try {
            await IdentityEndpoints.provisionForEvent(
              identityId, 
              event.id, 
              request
            )
            
            // Update the identity in the store
            const updatedIdentities = identities.map(identity => {
              if (identity.id === identityId) {
                return {
                  ...identity,
                  roleId: request.roleId,
                  secondaryRoleName: request.secondaryRoleName
                }
              }
              return identity
            })
            
            set({ identities: updatedIdentities })
            
            const updatedIdentity = updatedIdentities.find(i => i.id === identityId)!
            return updatedIdentity
          } catch (error) {
            console.error('Error in QuickProvisioningStore', error)
            throw error
          }
        },
        
        deleteProvisioning: async (identityId: number) => {
          const { event, identities } = get()
          if (!event) throw new Error('Event not loaded')
          
          try {
            await IdentityEndpoints.deleteEventProvisioning(
              identityId, 
              event.id
            )
            
            // Update the identity in the store
            const updatedIdentities = identities.map(identity => {
              if (identity.id === identityId) {
                return {
                  ...identity,
                  roleId: null,
                  secondaryRoleName: null
                }
              }
              return identity
            })
            
            set({ identities: updatedIdentities })
            
            const updatedIdentity = updatedIdentities.find(i => i.id === identityId)!
            return updatedIdentity
          } catch (error) {
            console.error('Error in QuickProvisioningStore', error)
            throw error
          }
        },
        
        provisionMultipleForEvent: async (
          request: EventIdentityProvisionMultipleRequest
        ) => {
          const eventId = (get() as any)._eventId
          const { identities } = get()
          
          try {
            await IdentityEndpoints.provisionMultipleForEvent(eventId, request)
            
            // Update the identities in the store
            const updatedIdentities = identities.map(identity => {
              if (request.identityIds.includes(identity.id)) {
                return {
                  ...identity,
                  roleId: request.roleId,
                  accreditationLevelId: request.accreditationLevelId,
                  secondaryRoleName: request.secondaryRoleName
                }
              }
              return identity
            })
            
            set({ identities: updatedIdentities })
          } catch (error) {
            console.error('Error in QuickProvisioningStore', error)
            throw error
          }
        },
        
        // Loading helpers
        startLoading: () => set((state) => ({ 
          waitingFor: state.waitingFor + 1 
        })),
        
        doneLoading: () => set((state) => ({ 
          waitingFor: Math.max(0, state.waitingFor - 1) 
        })),
        
        // Computed getters
        isLoading: () => get().waitingFor > 0,
        
        identitiesSorted: () => {
          return get().identities
            .slice()
            .sort(
              (a, b) =>
                a.firstName.localeCompare(b.firstName) ||
                a.lastName.localeCompare(b.lastName)
            )
        },
        
        orderedRoles: () => {
          const { event } = get()
          if (!event) return []
          
          const schedulable = event.roles.filter(r => r.schedulable)
          const nonSchedulable = event.roles.filter(r => !r.schedulable)
          
          return [
            ...schedulable.sort((a, b) => a.internalName > b.internalName ? 1 : -1),
            ...nonSchedulable.sort((a, b) => a.internalName > b.internalName ? 1 : -1)
          ]
        }
      }),
      {
        name: `quick-provisioning-store-${eventId}`,
      }
    )
  )
}

// Export a function to create quick provisioning stores
export const useQuickProvisioningStore = (eventId: number) => {
  // Create a new store instance for each event ID
  const store = createQuickProvisioningStore(eventId)
  
  // Initialize the store with the event ID
  store.getState().initialize(eventId)
  
  return store
}